import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import { saveAs } from 'file-saver';
import { authStorage } from '../../utils/storage';
import {
  FileText,
  FileSpreadsheet,
  File,
  Image,
  Paperclip,
  Upload,
  Download,
  Trash2,
  Plus,
  Info,
  X
} from 'lucide-react';
import './DocumentManager.css';

const DOC_TYPES = [
  { value: 'contract', label: 'Contract' },
  { value: 'amendment', label: 'Amendment' },
  { value: 'mou', label: 'MOU' },
  { value: 'constitution', label: 'Constitution' },
  { value: 'bylaws', label: 'Bylaws' },
  { value: 'policy', label: 'Policy' },
  { value: 'discipline', label: 'Discipline' },
  { value: 'other', label: 'Other' }
];

// Document types that have no contract term, so the "Years" field is optional for
// them (e.g. a constitution or bylaws). Years stays required for every other type.
const NO_TERM_DOC_TYPES = new Set(['constitution', 'bylaws']);

// Contract years are split years ("25/26"), matching how contracts actually
// run — a whole-year picker couldn't represent a July-June contract and left
// half-year gaps. Options span next year's contract back 12 contract years.
const toSpan = (startYear) => `${String(startYear).slice(-2)}/${String(startYear + 1).slice(-2)}`;
const buildYearOptions = () => {
  const current = new Date().getFullYear();
  const span = 12;
  const years = [];
  for (let i = 0; i < span; i += 1) {
    years.push(toSpan(current + 1 - i));
  }
  return years;
};

// Sort key that handles both formats: "25/26" -> 2025, legacy "2025" -> 2025.
const yearSortKey = (v) => {
  const m = String(v).match(/^(\d{2})\/\d{2}$/);
  if (m) return 2000 + Number(m[1]);
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Accept manual entry as "25/26", "2025/2026", or a bare start year (-> span).
const parseManualYear = (raw) => {
  const val = String(raw || '').trim();
  if (!val) return null;
  let m = val.match(/^(\d{2})\s*\/\s*(\d{2})$/);
  if (m) return `${m[1]}/${m[2]}`;
  m = val.match(/^(\d{4})\s*\/\s*\d{4}$/);
  if (m) return toSpan(Number(m[1]));
  m = val.match(/^(\d{4})$/);
  if (m) {
    const y = Number(m[1]);
    if (y < 1900 || y > 2100) return null;
    return toSpan(y);
  }
  return null;
};

const formatYearsValue = (value) => {
  if (!value) return '—';
  const arr = Array.isArray(value)
    ? value
    : String(value)
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
  if (!arr.length) return '—';
  return arr.join(', ');
};

const suggestDisplayName = (file) => {
  if (!file || !file.name) return '';
  const extIndex = file.name.lastIndexOf('.');
  const base = extIndex > -1 ? file.name.slice(0, extIndex) : file.name;
  return base;
};

const DocumentManager = ({ unitId, unitName, unitLocal }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [showDropzone, setShowDropzone] = useState(true);
  const [previewModal, setPreviewModal] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    displayName: '',
    docType: 'contract',
    docTypeOther: '',
    years: [],
    description: ''
  });

  const yearOptions = useMemo(() => buildYearOptions(), []);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/units/${unitId}/documents`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
        setSelectedDocs([]);
      } else {
        console.error('Document fetch failed:', response.status, response.statusText);
        if (response.status === 404) {
          setDocuments([]);
        } else if (response.status === 401) {
          setError('Authentication required. Please log in again.');
        } else {
          setError(`Failed to load documents (${response.status})`);
        }
      }
    } catch (err) {
      setError('Error loading documents');
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  }, [unitId]);

  useEffect(() => {
    fetchDocuments();
    setSelectedDocs([]);
    setUploadQueue([]);
    setShowUpload(false);
    setShowDropzone(true);
  }, [unitId, fetchDocuments]);

  useEffect(() => () => {
    uploadQueue.forEach(item => revokePreview(item.previewUrl));
  }, [uploadQueue]);



  const makePreview = (file) => {
    if (!file || !file.type) return null;
    const type = file.type;
    if (type.startsWith('image/') || type === 'application/pdf') {
      try { return { url: URL.createObjectURL(file), type: type.startsWith('image/') ? 'image' : 'pdf' }; }
      catch (_) { return null; }
    }
    return null;
  };

  const revokePreview = (url) => {
    if (!url) return;
    try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
  };

  // File upload handler (stage files, do not upload immediately)
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      const rejectionMessages = fileRejections.map(({ file, errors }) => {
        const errorMessages = errors.map(e => {
          if (e.code === 'file-invalid-type') return `${file.name}: Invalid file type`;
          if (e.code === 'file-too-large') return `${file.name}: File too large (max 50MB)`;
          return `${file.name}: ${e.message}`;
        });
        return errorMessages.join(', ');
      });
      setError(`File rejection: ${rejectionMessages.join('; ')}`);
      return;
    }

    if (!acceptedFiles.length) return;

    setError('');
    setSuccess('');
    setShowUpload(true);

    const additions = acceptedFiles.map(file => {
      const preview = makePreview(file);
      return {
        id: `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        displayName: suggestDisplayName(file),
        docType: 'contract',
        docTypeOther: '',
        years: [],
        yearInput: '',
        description: '',
        previewUrl: preview?.url || null,
        previewType: preview?.type || null
      };
    });

    setUploadQueue(prev => [...prev, ...additions]);
    setShowDropzone(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'application/vnd.ms-excel': [],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [],
      'application/vnd.ms-powerpoint': [],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': [],
      'application/rtf': [],
      'text/plain': [],
      'text/csv': [],
      'text/html': [],
      'image/jpeg': [],
      'image/png': [],
      'image/gif': [],
      'image/bmp': [],
      'image/webp': []
    },
    maxSize: 50 * 1024 * 1024,
    multiple: true,
    disabled: uploading
  });

  const updateQueueItem = (id, patch) => {
    setUploadQueue(prev => prev.map(item => (item.id === id ? { ...item, ...patch } : item)));
  };

  const toggleYear = (id, year) => {
    setUploadQueue(prev => prev.map(item => {
      if (item.id !== id) return item;
      const years = new Set(item.years || []);
      if (years.has(year)) years.delete(year);
      else years.add(year);
      const ordered = Array.from(years).sort((a, b) => yearSortKey(b) - yearSortKey(a));
      return { ...item, years: ordered };
    }));
  };

  const addManualYear = (id) => {
    setUploadQueue(prev => prev.map(item => {
      if (item.id !== id) return item;
      const spanVal = parseManualYear(item.yearInput);
      if (!spanVal) {
        setError('Enter a contract year like 25/26 (or a start year like 2025).');
        return item;
      }
      const years = new Set(item.years || []);
      years.add(spanVal);
      const ordered = Array.from(years).sort((a, b) => yearSortKey(b) - yearSortKey(a));
      setError('');
      return { ...item, years: ordered, yearInput: '' };
    }));
  };

  const removeQueuedFile = (id) => {
    setUploadQueue(prev => {
      const item = prev.find(i => i.id === id);
      if (item && item.previewUrl) revokePreview(item.previewUrl);
      return prev.filter(i => i.id !== id);
    });
  };

  const resetQueue = (clearMessages = false) => {
    uploadQueue.forEach(item => revokePreview(item.previewUrl));
    setUploadQueue([]);
    if (clearMessages) {
      setSuccess('');
      setError('');
    }
    setShowDropzone(true);
  };

  const normalizeDocType = (val = '') => {
    const v = String(val || '').trim().toLowerCase();
    if (v === 'disciple') return 'discipline';
    return v || 'contract';
  };

  const handleUpload = async () => {
    if (!uploadQueue.length) {
      setError('Add at least one file before uploading.');
      return;
    }

    // Validate entries
    for (const item of uploadQueue) {
      if (!item.displayName || !item.displayName.trim()) {
        setError(`Please choose a filename for ${item.file.name}.`);
        return;
      }
      const docType = normalizeDocType(item.docType);
      // Years are required for term documents (contracts, etc.) but optional for
      // no-term types like constitutions and bylaws.
      if (!NO_TERM_DOC_TYPES.has(docType) && (!item.years || item.years.length === 0)) {
        setError(`Select one or more years for ${item.file.name}.`);
        return;
      }
      if (docType === 'other' && (!item.docTypeOther || !item.docTypeOther.trim())) {
        setError(`Provide a custom type for ${item.file.name}.`);
        return;
      }
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    const metadata = uploadQueue.map(item => ({
      clientName: item.file.name,
      displayName: item.displayName.trim(),
      display_name: item.displayName.trim(), // server fallback
      years: item.years,
      docType: normalizeDocType(item.docType),
      docTypeOther: item.docTypeOther,
      description: item.description,
      unitLocal,
      document_years: item.years
    }));

    uploadQueue.forEach((item, idx) => {
      formData.append('files', item.file);
      // flat fields so backend can read even if metadata JSON parse fails
      formData.append('display_names[]', item.displayName.trim());
      formData.append('doc_types[]', normalizeDocType(item.docType));
      formData.append('doc_type_others[]', item.docTypeOther || '');
      formData.append('document_years[]', (item.years || []).join(','));
      formData.append('descriptions[]', item.description || '');
    });
    formData.append('metadata', JSON.stringify(metadata));

    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/units/${unitId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || 'Files uploaded successfully');
        setShowUpload(false);
        resetQueue();
        fetchDocuments();
      } else {
        let message = 'Upload failed';
        try {
          const err = await response.json();
          message = err.message || message;
        } catch (_) { /* ignore parse errors */ }
        setError(message);
      }
    } catch (err) {
      setError('Error uploading files');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      setShowDropzone(true);
    }
  };

  // Download single document
  const downloadDocument = async (documentId, originalName) => {
    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/units/${unitId}/documents/${documentId}/download`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        saveAs(blob, originalName);
      } else {
        setError('Failed to download document');
      }
    } catch (err) {
      setError('Error downloading document');
      console.error('Download error:', err);
    }
  };

  // Download multiple documents
  const downloadMultiple = async () => {
    if (selectedDocs.length === 0) {
      setError('Please select documents to download');
      return;
    }

    setLoading(true);
    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/units/${unitId}/documents/download-multiple`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ documentIds: selectedDocs })
      });

      if (response.ok) {
        const blob = await response.blob();
        saveAs(blob, `${unitName}_documents.zip`);
        setSelectedDocs([]);
      } else {
        setError('Failed to download documents');
      }
    } catch (err) {
      setError('Error downloading documents');
      console.error('Download error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Delete document
  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = authStorage.getToken();
      const response = await fetch(`/api/units/${unitId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        setSuccess('Document deleted successfully');
        fetchDocuments();
      } else {
        let message = 'Failed to delete document';
        try {
          const errorData = await response.json();
          message = errorData.message || message;
        } catch (_) { /* ignore */ }
        setError(message);
      }
    } catch (err) {
      setError('Error deleting document');
      console.error('Delete error:', err);
    }
  };

  // Toggle document selection
  const toggleSelection = (docId) => {
    setSelectedDocs(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Select all documents
  const selectAll = () => {
    if (selectedDocs.length === documents.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(documents.map(doc => doc.id));
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FileText size={16} />;
    if (fileType?.includes('word') || fileType?.includes('document')) return <File size={16} />;
    if (fileType?.includes('excel') || fileType?.includes('sheet')) return <FileSpreadsheet size={16} />;
    if (fileType?.includes('image')) return <Image size={16} />;
    return <Paperclip size={16} />;
  };

  const openEdit = (doc) => {
    setEditingId(doc.id);
    setEditForm({
      displayName: doc.display_name || doc.displayName || doc.original_name || '',
      docType: normalizeDocType(doc.doc_type || doc.category),
      docTypeOther: doc.doc_type_other || '',
      years: formatYearsValue(doc.document_years) === '—'
        ? []
        : String(doc.document_years)
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
            .map(v => Number(v) || v),
      description: doc.description || ''
    });
    setError('');
    setSuccess('');
  };

  const updateEditForm = (patch) => setEditForm(prev => ({ ...prev, ...patch }));

  const toggleEditYear = (year) => {
    setEditForm(prev => {
      const years = new Set(prev.years || []);
      if (years.has(year)) years.delete(year); else years.add(year);
      return { ...prev, years: Array.from(years).sort((a, b) => yearSortKey(b) - yearSortKey(a)) };
    });
  };

  const addEditManualYear = () => {
    const spanVal = parseManualYear(editForm.yearInput);
    if (!spanVal) {
      setError('Enter a contract year like 25/26 (or a start year like 2025).');
      return;
    }
    setEditForm(prev => ({
      ...prev,
      years: Array.from(new Set([...(prev.years || []), spanVal])).sort((a, b) => yearSortKey(b) - yearSortKey(a)),
      yearInput: ''
    }));
    setError('');
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!editForm.displayName || !editForm.displayName.trim()) {
      setError('Please enter a display name.');
      return;
    }
    const docType = normalizeDocType(editForm.docType);
    if (docType === 'other' && !(editForm.docTypeOther || '').trim()) {
      setError('Provide a custom type for this document.');
      return;
    }
    try {
      const token = authStorage.getToken();
      const res = await fetch(`/api/units/${unitId}/documents/${editingId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          display_name: editForm.displayName.trim(),
          doc_type: docType,
          doc_type_other: editForm.docTypeOther,
          description: editForm.description,
          document_years: editForm.years
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.message || 'Failed to update document');
        return;
      }
      setSuccess('Document updated');
      setEditingId(null);
      setEditForm({ displayName: '', docType: 'contract', docTypeOther: '', years: [], description: '' });
      fetchDocuments();
    } catch (e) {
      console.error('Edit save failed', e);
      setError('Failed to update document');
    }
  };

  const renderDocType = (doc) => {
    const type = normalizeDocType(doc.doc_type || doc.category);
    const label = type === 'other'
      ? (doc.doc_type_other || 'Other')
      : DOC_TYPES.find(t => t.value === type)?.label || type;
    return (
      <span className={`category-badge category-${type}`} title={label}>
        {label}
      </span>
    );
  };

  const renderUploadQueue = () => (
    <div className="upload-section">
      <div className="upload-step">
        <div className="upload-step-header">
          <span>Step 1: Add files</span>
          {uploadQueue.length > 0 && (
            <button className="text-btn" onClick={() => setShowDropzone(s => !s)}>
              {showDropzone ? 'Hide' : 'Add more files'}
            </button>
          )}
        </div>
        {showDropzone && (
          <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'disabled' : ''}`}>
            <input {...getInputProps()} />
            {uploading ? (
              <p>Uploading...</p>
            ) : (
              <div className="dropzone-content">
                <Upload size={48} className="dropzone-icon" style={{ margin: '0 auto 10px' }} />
                <p>Drag & drop files here, or click to select</p>
                <div className="dropzone-hint">
                  <p style={{ marginBottom: '8px' }}>
                    Files stay here until you complete details and submit.
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
                    <Info size={16} />
                    <span>Max size 50MB. PDFs, Office docs, text, images supported.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {uploadQueue.length > 0 && (
        <div className="upload-step">
          <div className="upload-step-header">Step 2: Describe each file</div>
          <div className="upload-queue">
            {uploadQueue.map(item => (
              <div className="file-card" key={item.id}>
                <div className="file-card-body">
                  <div>
                    <div className="file-card-header">
                      <div>
                        <div className="file-name">{item.file.name}</div>
                        <div className="file-sub">{formatFileSize(item.file.size)} • {item.file.type || 'Unknown type'}</div>
                      </div>
                      <button className="btn-icon" onClick={() => removeQueuedFile(item.id)} aria-label="Remove file">
                        <X size={16} />
                      </button>
                    </div>

                    <div className="metadata-grid">
                      <div className="form-group">
                        <label>
                          Filename to display
                          <span className="label-hint">e.g., 2024-2027 CBA - Custodians</span>
                        </label>
                        <input
                          type="text"
                          value={item.displayName}
                          onChange={(e) => updateQueueItem(item.id, { displayName: e.target.value })}
                          placeholder="Enter a user-friendly name"
                        />
                      </div>

                      <div className="form-group">
                        <label>Type</label>
                        <select
                          value={item.docType}
                          onChange={(e) => updateQueueItem(item.id, { docType: e.target.value })}
                        >
                          {DOC_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        {normalizeDocType(item.docType) === 'other' && (
                          <input
                            className="inline-input"
                            type="text"
                            value={item.docTypeOther}
                            placeholder="Enter custom type"
                            onChange={(e) => updateQueueItem(item.id, { docTypeOther: e.target.value })}
                            style={{ marginTop: 6 }}
                          />
                        )}
                      </div>

                      <div className="form-group">
                        <label>Years {NO_TERM_DOC_TYPES.has(normalizeDocType(item.docType)) ? '(optional for this type)' : '(select all that apply)'}</label>
                        <div className="year-chips">
                          {yearOptions.map(year => (
                            <button
                              type="button"
                              key={year}
                              className={`year-chip ${item.years.includes(year) ? 'selected' : ''}`}
                              onClick={() => toggleYear(item.id, year)}
                            >
                              {year}
                            </button>
                          ))}
                        </div>
                        <div className="year-manual">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={item.yearInput}
                            onChange={(e) => updateQueueItem(item.id, { yearInput: e.target.value })}
                            placeholder="Add another (e.g. 25/26)"
                          />
                          <button type="button" className="btn-secondary" onClick={() => addManualYear(item.id)}>Add</button>
                        </div>
                        <div className="selected-years">Selected: {item.years.length ? item.years.join(', ') : 'None yet'}</div>
                      </div>

                      <div className="form-group">
                        <label>Local</label>
                        <div className="static-pill">{unitLocal ? `Local ${unitLocal}` : 'Not available'}</div>
                      </div>

                      <div className="form-group">
                        <label>Description (optional)</label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateQueueItem(item.id, { description: e.target.value })}
                          placeholder="Notes to help search later"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="file-preview">
                    {item.previewUrl ? (
                      <button
                        type="button"
                        className="preview-button"
                        onClick={() => setPreviewModal({ src: item.previewUrl, name: item.file.name, type: item.previewType })}
                        aria-label="Preview file"
                      >
                        {item.previewType === 'pdf' ? (
                          <div className="pdf-thumb">
                            <embed src={item.previewUrl} type="application/pdf" />
                            <span className="preview-hint">PDF — click to enlarge</span>
                          </div>
                        ) : (
                          <>
                            <img src={item.previewUrl} alt={item.file.name} />
                            <span className="preview-hint">Click to enlarge</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="preview-placeholder">
                        <Paperclip size={18} />
                        <span>{item.file.type || 'File'}</span>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>

          <div className="upload-actions">
            <div className="upload-actions-left">
              <button className="btn-secondary" onClick={() => resetQueue(true)} disabled={uploading}>Clear pending files</button>
            </div>
            <div className="upload-actions-right">
              <button className="btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Saving...' : 'Save and Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="document-manager">
      <div className="document-actions">
        <button
          className="btn-primary"
          onClick={() => {
            const next = !showUpload;
            setShowUpload(next);
            if (!next) resetQueue(true);
          }}
        >
          {showUpload ? 'Cancel Upload' : <><Plus size={16} style={{ marginRight: '4px', verticalAlign: 'middle' }} />Upload Documents</>}
        </button>
        {selectedDocs.length > 0 && (
          <button
            className="btn-secondary"
            onClick={downloadMultiple}
            disabled={loading}
          >
            Download Selected ({selectedDocs.length})
          </button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {showUpload && renderUploadQueue()}

      {previewModal && (
        <div className="preview-modal" onClick={() => setPreviewModal(null)}>
          <div className="preview-modal-body" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <div className="file-name">{previewModal.name}</div>
              <button className="btn-icon" onClick={() => setPreviewModal(null)} aria-label="Close preview">
                <X size={18} />
              </button>
            </div>
            <div className="preview-modal-content">
              {previewModal.type === 'pdf' ? (
                <embed src={previewModal.src} type="application/pdf" />
              ) : (
                <img src={previewModal.src} alt={previewModal.name} />
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading documents...</div>
      ) : documents.length === 0 ? (
        <div className="no-documents">
          <p>No documents uploaded yet</p>
          <p className="hint">Click "Upload Documents" to add contracts and other files</p>
        </div>
      ) : (
        <div className="documents-table">
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedDocs.length === documents.length}
                    onChange={selectAll}
                  />
                </th>
                <th>Name</th>
                <th>Type</th>
                <th>Years</th>
                <th>Local</th>
                <th>Size</th>
                <th>Uploaded</th>
                <th>Uploaded By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map(doc => {
                const displayName = doc.display_name || doc.displayName || doc.original_name;
                const originalDifferent = displayName !== doc.original_name;
                return (
                  <tr key={doc.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => toggleSelection(doc.id)}
                      />
                    </td>
                    <td className="doc-name">
                      <div className="doc-title">
                        <span className="doc-icon" style={{ marginRight: '8px', display: 'inline-flex', verticalAlign: 'middle' }}>
                          {getFileIcon(doc.file_type)}
                        </span>
                        <span style={{ verticalAlign: 'middle' }}>{displayName}</span>
                      </div>
                      <div className="doc-subline">{originalDifferent ? `File: ${doc.original_name}` : doc.original_name}</div>
                      {doc.description && (
                        <span className="doc-description">{doc.description}</span>
                      )}
                    </td>
                    <td>{renderDocType(doc)}</td>
                    <td>{formatYearsValue(doc.document_years)}</td>
                    <td>{doc.unit_local || unitLocal || '—'}</td>
                    <td>{formatFileSize(doc.file_size)}</td>
                    <td>{new Date(doc.uploaded_at).toLocaleDateString()}</td>
                    <td>{doc.uploaded_by}</td>
                    <td className="doc-actions">
                      <button
                        className="btn-icon"
                        onClick={() => downloadDocument(doc.id, doc.original_name)}
                        title="Download"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => openEdit(doc)}
                        title="Edit metadata"
                      >
                        ✎
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        onClick={() => deleteDocument(doc.id)}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {editingId && (
            <div className="edit-panel">
              <div className="edit-header">
                <div className="edit-title">Edit document details</div>
                <button className="btn-icon" onClick={() => setEditingId(null)} aria-label="Close edit">×</button>
              </div>
              <div className="metadata-grid">
                <div className="form-group">
                  <label>Filename to display</label>
                  <input
                    type="text"
                    value={editForm.displayName}
                    onChange={(e) => updateEditForm({ displayName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={editForm.docType}
                    onChange={(e) => updateEditForm({ docType: e.target.value })}
                  >
                    {DOC_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  {normalizeDocType(editForm.docType) === 'other' && (
                    <input
                      className="inline-input"
                      type="text"
                      value={editForm.docTypeOther}
                      onChange={(e) => updateEditForm({ docTypeOther: e.target.value })}
                      placeholder="Custom type"
                      style={{ marginTop: 6 }}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Years {NO_TERM_DOC_TYPES.has(normalizeDocType(editForm.docType)) ? '(optional for this type)' : '(select all that apply)'}</label>
                  <div className="year-chips">
                    {yearOptions.map(year => (
                      <button
                        type="button"
                        key={year}
                        className={`year-chip ${editForm.years.includes(year) ? 'selected' : ''}`}
                        onClick={() => toggleEditYear(year)}
                      >
                        {year}
                      </button>
                    ))}
                  </div>
                  <div className="year-manual">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={editForm.yearInput || ''}
                      onChange={(e) => updateEditForm({ yearInput: e.target.value })}
                      placeholder="Add another (e.g. 25/26)"
                    />
                    <button type="button" className="btn-secondary" onClick={addEditManualYear}>Add</button>
                  </div>
                  <div className="selected-years">Selected: {editForm.years.length ? editForm.years.join(', ') : 'None yet'}</div>
                </div>
                <div className="form-group">
                  <label>Description (optional)</label>
                  <input
                    type="text"
                    value={editForm.description}
                    onChange={(e) => updateEditForm({ description: e.target.value })}
                    placeholder="Notes to help search later"
                  />
                </div>
              </div>
              <div className="edit-actions">
                <button className="btn-secondary" onClick={() => setEditingId(null)}>Cancel</button>
                <button className="btn-primary" onClick={saveEdit}>Save changes</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentManager;
